// app/components/OdometerVelocityModal.tsx
import { Portal, Modal, Text, Button, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';

interface OdometerVelocityModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  meanVelocityKmh: number;
  previousOdometer: number;
  newOdometer: number;
  timeDifferenceHours: number;
  isLoading?: boolean;
}

export const OdometerVelocityModal = ({
  visible,
  onDismiss,
  onConfirm,
  meanVelocityKmh,
  previousOdometer,
  newOdometer,
  timeDifferenceHours,
  isLoading = false,
}: OdometerVelocityModalProps) => {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();

  const odometerDifference = newOdometer - previousOdometer;

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={[
          {
            backgroundColor: theme.colors.surface,
            padding: 20,
            margin: 20,
            borderRadius: 8,
          },
          Platform.OS === 'web' ? {
            maxWidth: 500,
            width: '90%',
            alignSelf: 'center',
          } : {}
        ]}
      >
        <Text variant="headlineSmall" style={{ marginBottom: 20 }}>
          {t('odometerVelocityWarning.title', 'Odometer Verification')}
        </Text>
        
        <Text variant="bodyMedium" style={{ marginBottom: 16 }}>
          {t('odometerVelocityWarning.message', 'The odometer you entered suggests an average speed of {{velocity}} km/h between sessions.', {
            velocity: meanVelocityKmh.toFixed(1)
          })}
        </Text>

        <View style={{ marginBottom: 16, padding: 12, backgroundColor: theme.colors.surfaceVariant, borderRadius: 8 }}>
          <Text variant="bodySmall" style={{ marginBottom: 4 }}>
            {t('odometerVelocityWarning.previousOdometer', 'Previous odometer')}: {previousOdometer} km
          </Text>
          <Text variant="bodySmall" style={{ marginBottom: 4 }}>
            {t('odometerVelocityWarning.newOdometer', 'New odometer')}: {newOdometer} km
          </Text>
          <Text variant="bodySmall" style={{ marginBottom: 4 }}>
            {t('odometerVelocityWarning.difference', 'Difference')}: {odometerDifference} km
          </Text>
          <Text variant="bodySmall">
            {t('odometerVelocityWarning.timeDifference', 'Time difference')}: {timeDifferenceHours.toFixed(1)} {t('hours', { ns: 'translation' })}
          </Text>
        </View>

        <Text variant="bodyMedium" style={{ marginBottom: 20, color: theme.colors.error }}>
          {t('odometerVelocityWarning.question', 'Is this odometer reading correct?')}
        </Text>

        <Button
          mode="contained"
          onPress={onConfirm}
          loading={isLoading}
          disabled={isLoading}
          style={{ marginBottom: 10 }}
        >
          {t('odometerVelocityWarning.confirm', 'Yes, it\'s correct')}
        </Button>
        <Button
          mode="text"
          onPress={onDismiss}
          disabled={isLoading}
        >
          {t('cancel', { ns: 'translation' })}
        </Button>
      </Modal>
    </Portal>
  );
};

